/**
 * MagnetPage — /m/:slug
 *
 * Generic lead-magnet landing + capture + thank-you page.
 * Renders any magnet from CC's public feed by slug — no per-book pages.
 *
 * Flow:
 *   1. Fetch CC definition from /api/public/magnets/:slug.
 *   2. Render landing_html + email capture form (screen: 'landing').
 *   3. POST /capture → flip to 'thankyou' screen showing thankyou_html + CTA stack.
 *
 * CTA stack (rendered after thankyou_html):
 *   Path 1 — next event / program (funnel_target)
 *   Path 2 — sibling magnets from same theme (related_magnets)
 *   Path 3 — talk to Ron (talk_to)
 *
 * Two rendering models, chosen per body:
 *   - FRAGMENT (the generator's output): wrapped in TakerShell (TSB chrome); the
 *     capture form is a styled box; the thank-you adds the CtaStack + SMS opt-in.
 *   - FULL DOCUMENT (a bespoke magnet like lm-client-getting, whose landing /
 *     thank-you are complete HTML pages with their own header/footer/styles):
 *     rendered STANDALONE — CC owns 100% of the chrome. EE injects only the live
 *     capture form into the {{OPT_IN_FORM}} marker (via a portal, so the form
 *     inherits CC's own form styling) and renders nothing else around it.
 */

import { useState, useEffect, useRef, useMemo, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';
import { freeSeatThanksLine } from './freeSeatThanks';
import { CountrySelect } from './components/CountrySelect';
import { SmsOptIn } from './components/SmsOptIn';
import MagnetHeader, { type MagnetHeaderBrand } from './components/MagnetHeader';
import MagnetFooter from './components/MagnetFooter';
import {
  MAGNET_SUBMIT_LABEL,
  MagnetStructuredLanding,
  MagnetStructuredThankyou,
  type MagnetFunnelUrls,
} from './components/MagnetStructured';
import {
  parseMagnetLandingContent,
  parseMagnetThankyouContent,
} from './lib/magnetContent';
import { fetchWithRetry } from './lib/fetchRetry';
import { useBrandGtm } from './lib/useBrandGtm';
import { recordLandingView } from './api';
import { trackableVisitorId } from './attribution';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';

export interface MagnetRelated {
  slug: string;
  title: string;
  cta_url: string;
}

export interface MagnetTalkTo {
  url: string;
  label: string;
}

interface MagnetDef {
  slug: string;
  title: string;
  badge: string;
  source_book_title: string | null;
  source_book_author: string | null;
  blurb: string | null;
  landing_html: string | null;
  thankyou_html: string | null;
  /** Structured pure-copy content (v1) — parsed/validated client-side; null or
   *  malformed falls back to the legacy HTML fields above. */
  landing_content: unknown;
  thankyou_content: unknown;
  magnet_download_url: string | null;
  funnel_target_kind: string | null;
  funnel_target_url: string | null;
  related_magnets: MagnetRelated[];
  talk_to: MagnetTalkTo | null;
  /** EE sibling, merged in on load (never part of CC's shape): the Free
   *  tier's step-1 opt-in sentence for an entry magnet, else null. */
  free_seat_optin?: { key: string; version: number; wording: string } | null;
}

/** The capture's Free tier answer: present only for an entry magnet while
 *  the Free tier takes sign-ups. claim_url only when this request created
 *  the record (worker free-tier-core captureOutcome). */
type FreeSeat = { claim_url: string | null; thanks_line?: string | null } | null;
type OnCapture = (thankyouHtml: string | null, cta: CtaPayload, lead: { email: string; firstName: string; lastName: string }, freeSeat: FreeSeat) => void;

export interface CtaPayload {
  path1: { kind: string; url: string } | null;
  path2: MagnetRelated[] | null;
  path3: MagnetTalkTo | null;
}

export interface MagnetBrand extends MagnetHeaderBrand {
  gtmContainerId: string | null;
}

type Screen = 'loading' | 'landing' | 'thankyou' | 'not_found' | 'coming_soon' | 'error';

// ---------------------------------------------------------------------------
// Structured-render flag (Phase 2 parity gate — PASSED)
// ---------------------------------------------------------------------------
// LIVE since 2026-09-09: Michael approved the lm-client-getting side-by-side
// parity screenshots (spec Phase 2 §5), so structured content now renders by
// default wherever a magnet carries parseable landing_content/thankyou_content.
//   ?structured=0 forces the legacy HTML render (the emergency escape hatch —
//     flip this constant back to false for a fleet-wide revert),
//   ?structured=1 forces structured (now redundant, kept for symmetry).
// Magnets without structured content still take the legacy paths regardless.
const STRUCTURED_DEFAULT_LIVE = true;

export function structuredRenderEnabled(): boolean {
  try {
    const p = new URLSearchParams(window.location.search).get('structured');
    if (p === '1') return true;
    if (p === '0') return false;
  } catch { /* fall through */ }
  return STRUCTURED_DEFAULT_LIVE;
}

function utmFromSearch(): Record<string, string> {
  const p = new URLSearchParams(window.location.search);
  const fields = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid'];
  const out: Record<string, string> = {};
  for (const f of fields) {
    const v = p.get(f);
    if (v) out[f] = v;
  }
  return out;
}

function referrerFromDoc(): string | null {
  try { return document.referrer || null; } catch { return null; }
}

// The {{OPT_IN_FORM}} marker CC authors into the landing body, where EE mounts
// the live capture form. The optional :label suffix lets CC set the opt-in
// button text inline ({{OPT_IN_FORM:Send me the 117 ways}}).
const OPT_IN_RE = /\{\{\s*OPT_IN_FORM(?::([^}]*))?\s*\}\}/;

// Per-magnet opt-in button label fallback, used when the body doesn't carry an
// inline {{OPT_IN_FORM:label}}. Keeps the generic default for everything else.
const OPTIN_LABELS: Record<string, string> = {
  'lm-client-getting': 'Send me the 117 ways',
};
const DEFAULT_OPTIN_LABEL = 'Send me the summary';

// Split a landing body at the {{OPT_IN_FORM}} marker so the capture form renders
// exactly where CC placed it. No marker → form is appended after the body
// (legacy generator behaviour). Returns the resolved opt-in button label too.
function splitLanding(slug: string, html: string): { before: string; after: string; label: string } {
  const m = html.match(OPT_IN_RE);
  const label = (m?.[1]?.trim()) || OPTIN_LABELS[slug] || DEFAULT_OPTIN_LABEL;
  if (!m || m.index === undefined) return { before: html, after: '', label };
  return { before: html.slice(0, m.index), after: html.slice(m.index + m[0].length), label };
}

// A CC body is a FULL HTML document (its own <head>/<style>/chrome) vs a content
// fragment. Full docs render standalone — never wrapped in TakerShell, never
// squeezed into the 680px column, since CC owns their entire layout.
export function isFullDoc(html: string | null | undefined): boolean {
  return !!html && /<!doctype html|<html[\s>]/i.test(html);
}

// Prepare a full-doc landing for the portal: swap the {{OPT_IN_FORM}} marker for
// a stable mount node the form portals into (so it lands inside CC's own form
// card and inherits its styling). Returns the rewritten HTML + the opt-in label.
const OPT_IN_MOUNT_ID = 'ee-optin-mount';
// Anchor target for the full-doc header CTA when a landing body has no
// {{OPT_IN_FORM}} marker — the defensive fallback form box below gets this id
// instead so the CTA still has something to scroll to.
const FULLDOC_FALLBACK_FORM_ID = 'ee-optin-fallback';
function prepFullDocLanding(slug: string, html: string): { html: string; label: string; hasMarker: boolean } {
  const m = html.match(OPT_IN_RE);
  const label = (m?.[1]?.trim()) || OPTIN_LABELS[slug] || DEFAULT_OPTIN_LABEL;
  if (!m) return { html, label, hasMarker: false };
  return { html: html.replace(OPT_IN_RE, `<div id="${OPT_IN_MOUNT_ID}"></div>`), label, hasMarker: true };
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function CaptureForm({
  def,
  ctaLabel,
  variant = 'boxed',
  onSuccess,
}: {
  def: MagnetDef;
  /** Opt-in button label. CC can override per magnet via {{OPT_IN_FORM:Label}};
   *  defaults to a generic "Send me the summary". */
  ctaLabel: string;
  /** 'boxed' (default) uses EE's own field/button styling — for fragment magnets
   *  in TakerShell. 'bare' renders unstyled tags so CC's own form-card CSS paints
   *  them — used when the form is portaled into a full-doc landing's card. */
  variant?: 'boxed' | 'bare';
  onSuccess: OnCapture;
}) {
  const bare = variant === 'bare';
  // In bare mode, leave inputs/button unstyled so CC's `.lh-form-card input/button`
  // rules paint them; only error/helper/select get neutral inline styling.
  const inputStyle = bare ? undefined : { ...styles.input, width: '100%', boxSizing: 'border-box' as const };
  const rowInputStyle = bare ? { marginBottom: 8 } : styles.input;
  const btnStyle = bare ? undefined : styles.submitBtn;
  const formStyle = bare ? styles.formBare : styles.form;
  const selectStyle = bare ? styles.selectBare : { ...styles.input, width: '100%', boxSizing: 'border-box' as const };

  const [email,     setEmail]     = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [country,   setCountry]   = useState('');
  const [step,      setStep]      = useState<'email' | 'details'>('email');
  // The Free tier's step-1 opt-in: unticked, and only ever sent as a yes. A
  // box left alone sends nothing, so it is never recorded as a "no".
  const [optin,     setOptin]     = useState(false);
  const [busy,      setBusy]      = useState(false);
  const [error,     setError]     = useState<string | null>(null);

  async function capture(stage: 'email' | 'complete', extra: Record<string, unknown>) {
    const res = await fetch(`${API_BASE}/api/public/magnets/${def.slug}/capture`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        stage,
        first_referrer: referrerFromDoc(),
        ...utmFromSearch(),
        // Ties this browser to the person who claims the magnet (worker visitor-ties.ts).
        visitor_id: trackableVisitorId() ?? undefined,
        ...extra,
      }),
    });
    const data = await res.json() as {
      success?: boolean; error?: string; thankyou_html?: string | null; cta?: CtaPayload; free_seat?: FreeSeat;
    };
    if (!res.ok || !data.success) {
      setError(data.error || 'Something went wrong. Please try again.');
      return null;
    }
    return data;
  }

  // Step 1 — email only. Saves the lead; the summary is delivered after step 2.
  async function submitEmail(e: FormEvent) {
    e.preventDefault();
    if (!email) return;
    setBusy(true); setError(null);
    const data = await capture('email', optin && def.free_seat_optin ? { marketing_optin: true } : {});
    setBusy(false);
    if (data) setStep('details');
  }

  // Step 2 — first name + last name + country, all required (the minimum
  // every opt-in on the engine captures, 2026-09-13). Delivers the summary.
  const detailsComplete = firstName.trim() && lastName.trim() && country;
  async function submitDetails(e: FormEvent) {
    e.preventDefault();
    if (!detailsComplete) return;
    setBusy(true); setError(null);
    const data = await capture('complete', {
      first_name: firstName.trim(), last_name: lastName.trim(), country,
    });
    if (!data) { setBusy(false); return; }
    onSuccess(data.thankyou_html ?? null, data.cta ?? { path1: null, path2: null, path3: null }, { email, firstName, lastName }, data.free_seat ?? null);
  }

  if (step === 'details') {
    return (
      <form onSubmit={submitDetails} style={formStyle}>
        <p style={{ ...styles.privacyNote, marginTop: 0, marginBottom: 4 }}>Where should we send it? Just your name and where you're based.</p>
        <div style={bare ? undefined : styles.fieldRow}>
          <input type="text" placeholder="First name" value={firstName} autoFocus required
            onChange={e => setFirstName(e.target.value)} style={rowInputStyle} aria-label="First name" />
          <input type="text" placeholder="Last name" value={lastName} required
            onChange={e => setLastName(e.target.value)} style={rowInputStyle} aria-label="Last name" />
        </div>
        <CountrySelect value={country} onChange={setCountry} style={selectStyle} />
        {error && <p style={styles.errorText}>{error}</p>}
        <button type="submit" disabled={busy || !detailsComplete} style={btnStyle}>
          {busy ? 'Sending…' : ctaLabel}
        </button>
        {!bare && <p style={styles.privacyNote}>No spam. Unsubscribe any time.</p>}
      </form>
    );
  }

  return (
    <form onSubmit={submitEmail} style={formStyle}>
      <input
        type="email"
        placeholder="Your email address"
        value={email}
        onChange={e => setEmail(e.target.value)}
        required
        autoFocus
        style={inputStyle}
        aria-label="Email address"
      />
      {def.free_seat_optin && (
        <label style={styles.optin} data-testid="magnet-free-seat-optin">
          <input type="checkbox" checked={optin} onChange={e => setOptin(e.target.checked)} style={{ marginTop: 3 }} />
          <span>{def.free_seat_optin.wording}</span>
        </label>
      )}
      {error && <p style={styles.errorText}>{error}</p>}
      <button type="submit" disabled={busy || !email} style={btnStyle}>
        {busy ? 'Sending…' : ctaLabel}
      </button>
      {!bare && <p style={styles.privacyNote}>No spam. Unsubscribe any time.</p>}
    </form>
  );
}

// Full-document landing: render CC's complete page as-is and portal the live
// capture form into the {{OPT_IN_FORM}} marker so it sits inside CC's own form
// card (inheriting its styling). No TakerShell — CC owns the body chrome —
// but a slim MagnetHeader (brand logo + CTA) renders above it and a matching
// MagnetFooter (brand + required Privacy/Terms links) renders below it (see
// MagnetHeader.tsx / MagnetFooter.tsx for why these are the sanctioned
// exceptions to "full docs get zero EE chrome").
function FullDocLanding({
  def,
  brand,
  onSuccess,
}: {
  def: MagnetDef;
  brand: MagnetBrand | null;
  onSuccess: OnCapture;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [mount, setMount] = useState<HTMLElement | null>(null);
  const prepped = useMemo(() => prepFullDocLanding(def.slug, def.landing_html ?? ''), [def.slug, def.landing_html]);

  useEffect(() => {
    const node = containerRef.current?.querySelector(`#${OPT_IN_MOUNT_ID}`) as HTMLElement | null;
    setMount(node);
  }, [prepped.html]);

  const scrollToOptIn = () => {
    const target = document.getElementById(prepped.hasMarker ? OPT_IN_MOUNT_ID : FULLDOC_FALLBACK_FORM_ID);
    target?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <MagnetHeader brand={brand} ctaLabel={prepped.label} onCta={scrollToOptIn} />
      <div ref={containerRef} dangerouslySetInnerHTML={{ __html: prepped.html }} />
      {mount && createPortal(
        <CaptureForm def={def} ctaLabel={prepped.label} variant="bare" onSuccess={onSuccess} />,
        mount,
      )}
      {/* Defensive: a full doc with no marker still gets a usable form. */}
      {!prepped.hasMarker && (
        <div id={FULLDOC_FALLBACK_FORM_ID} style={{ ...styles.captureBox, maxWidth: 560, margin: '24px auto 48px' }}>
          <CaptureForm def={def} ctaLabel={prepped.label} onSuccess={onSuccess} />
        </div>
      )}
      <MagnetFooter brand={brand} />
    </>
  );
}

/**
 * The Free tier's thank-you line (mct_70dc6360-1e5 item 5). The SAME words
 * whether or not the address was already known, so a visitor cannot tell
 * whether someone is a member: every unclaimed sign-up is emailed its link.
 * The inline button appears only when this request created the record, and
 * is a plain <a href> full page load — never a router link — so the token
 * never enters this page's history or its tag manager's page views; the claim
 * page strips it at once and loads no tags.
 * ENGINE DEFAULT TEXT: Command Centre will supply the real wording.
 */
function FreeSeatThankyou({ freeSeat }: { freeSeat: FreeSeat }) {
  if (!freeSeat) return null;
  return (
    <div style={styles.freeSeat} data-testid="magnet-free-seat">
      <p style={{ margin: 0, lineHeight: 1.6 }}>{freeSeatThanksLine(freeSeat)}</p>
      {freeSeat.claim_url && (
        <a href={freeSeat.claim_url} rel="noreferrer" style={{ ...styles.ctaPrimary, marginTop: 12 }} data-testid="magnet-free-seat-claim">
          Claim your free seat now
        </a>
      )}
    </div>
  );
}

export function CtaStack({ cta }: { cta: CtaPayload }) {
  const hasCtas = cta.path1 || (cta.path2?.length) || cta.path3;
  if (!hasCtas) return null;

  return (
    <div style={styles.ctaStack}>
      {cta.path1 && (
        <a href={cta.path1.url} style={styles.ctaPrimary}>
          Join the next event →
        </a>
      )}

      {cta.path2 && cta.path2.length > 0 && (
        <div style={styles.ctaSection}>
          <p style={styles.ctaSectionLabel}>More summaries you might like</p>
          <div style={styles.ctaGrid}>
            {cta.path2.map(m => (
              <a key={m.slug} href={m.cta_url} style={styles.ctaCard}>
                {m.title}
              </a>
            ))}
          </div>
        </div>
      )}

      {cta.path3 && (
        <div style={styles.ctaSection}>
          <p style={styles.ctaSectionLabel}>Want to talk through what you read?</p>
          <a href={cta.path3.url} style={styles.ctaSecondary}>
            {cta.path3.label || 'Book a call with our team'}
          </a>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function MagnetPage() {
  const { slug } = useParams<{ slug: string }>();
  const [screen,      setScreen]      = useState<Screen>('loading');
  const [def,         setDef]         = useState<MagnetDef | null>(null);
  const [brand,       setBrand]       = useState<MagnetBrand | null>(null);
  const [coverUrl,    setCoverUrl]    = useState<string | null>(null);
  const [funnelUrls,  setFunnelUrls]  = useState<MagnetFunnelUrls>({ funnel: null, cbl: null, call: null });
  const [thankyouHtml, setThankyouHtml] = useState<string | null>(null);
  const [cta,         setCta]         = useState<CtaPayload>({ path1: null, path2: null, path3: null });
  const [lead,        setLead]        = useState<{ email: string; firstName: string; lastName: string } | null>(null);
  const [freeSeat,    setFreeSeat]    = useState<FreeSeat>(null);
  const [retryNonce,  setRetryNonce]  = useState(0);

  // Brand-aware GTM — every magnet now carries a resolved brand payload
  // (worker/src/magnet-routes.ts). `ready` gates on screen !== 'loading' so a
  // non-TSB brand's container is never preceded by the host-fallback TSB
  // container (two containers on one page double-counts pageviews).
  useBrandGtm(brand?.gtmContainerId, screen !== 'loading');

  // Landing-views beacon — fires once per mount for funnel tracking. Same
  // convention as WaitlistPage/LetsTalkPage/ReplayPage/ProgramSalesPage; see
  // "Landing-views beacon coverage" in sop-ee-anon-visitor-attribution.md —
  // this page was missing it (fixed 2026-09-18), meaning magnet visit-to-lead
  // conversion had zero pre-capture visitor tracking (magnet_events only
  // starts once a lead_id exists, i.e. after capture).
  useEffect(() => {
    if (slug) recordLandingView(slug);
  }, [slug]);

  // Hoisted so TakerShell's nav CTA label matches the one used inside the
  // capture box below (both derive from the same {{OPT_IN_FORM:label}} marker).
  const landingLabel = useMemo(
    () => def ? splitLanding(def.slug, def.landing_html ?? '').label : undefined,
    [def],
  );

  useEffect(() => {
    if (!slug) { setScreen('not_found'); return; }

    setScreen('loading');
    fetchWithRetry(`${API_BASE}/api/public/magnets/${encodeURIComponent(slug)}`)
      .then(r => r.json() as Promise<{
        lead_magnet?: MagnetDef; brand?: MagnetBrand; error?: string; coming_soon?: boolean;
        cover_url?: string | null; funnel_urls?: MagnetFunnelUrls;
        free_seat_optin?: MagnetDef['free_seat_optin'];
      }>)
      .then(data => {
        setBrand(data.brand ?? null);
        if (data.coming_soon) { setScreen('coming_soon'); return; }
        if (!data.lead_magnet) { setScreen('not_found'); return; }
        setDef({ ...data.lead_magnet, free_seat_optin: data.free_seat_optin ?? null });
        setCoverUrl(data.cover_url ?? null);
        setFunnelUrls(data.funnel_urls ?? { funnel: null, cbl: null, call: null });
        setScreen('landing');
      })
      .catch(() => setScreen('error'));
  }, [slug, retryNonce]);

  function handleCaptureSuccess(html: string | null, ctaData: CtaPayload, leadData: { email: string; firstName: string; lastName: string }, seat: FreeSeat) {
    setFreeSeat(seat);
    setThankyouHtml(html);
    setCta(ctaData);
    setLead(leadData);
    setScreen('thankyou');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Render preference (spec Phase 2 §2): structured content present AND the
  // structured flag on → EE's own structured render; otherwise the legacy
  // landing_html/thankyou_html paths below, byte-for-byte unchanged. The
  // parsers are the gate — malformed content (or content missing the required
  // attribution) degrades to the legacy render, never to a broken page.
  const structuredOn = structuredRenderEnabled();
  if (structuredOn && def) {
    const landingContent = parseMagnetLandingContent(def.landing_content);
    const thankyouContent = parseMagnetThankyouContent(def.thankyou_content);
    if (screen === 'landing' && landingContent) {
      return (
        <MagnetStructuredLanding
          content={landingContent}
          brand={brand}
          coverUrl={coverUrl}
          coverAlt={def.title}
        >
          <div className="eelm-capture">
            <CaptureForm def={def} ctaLabel={MAGNET_SUBMIT_LABEL} variant="bare" onSuccess={handleCaptureSuccess} />
          </div>
        </MagnetStructuredLanding>
      );
    }
    if (screen === 'thankyou' && thankyouContent) {
      return (
        <>
          <FreeSeatThankyou freeSeat={freeSeat} />
          <MagnetStructuredThankyou
            content={thankyouContent}
            brand={brand}
            funnelUrls={funnelUrls}
          />
        </>
      );
    }
  }

  // Full-document bodies render STANDALONE — CC owns the body chrome. EE adds
  // only the capture form (landing), the MagnetHeader, and the MagnetFooter
  // (all screens); no TakerShell, no CtaStack, no SMS block, and thank-you
  // keeps CC's own inline CTAs.
  if (screen === 'landing' && def && isFullDoc(def.landing_html)) {
    return <FullDocLanding def={def} brand={brand} onSuccess={handleCaptureSuccess} />;
  }
  if (screen === 'thankyou' && def && isFullDoc(thankyouHtml)) {
    return (
      <>
        <MagnetHeader brand={brand} />
        <FreeSeatThankyou freeSeat={freeSeat} />
        <div className="magnet-thankyou-fulldoc" dangerouslySetInnerHTML={{ __html: thankyouHtml! }} />
        <MagnetFooter brand={brand} />
      </>
    );
  }

  return (
    <TakerShell
      logoUrl={brand?.logoWhiteUrl ?? brand?.logoUrl}
      logoAlt={brand?.name}
      logoHref={brand?.logoLinkUrl}
      ctaHref={screen === 'landing' ? '#ee-optin' : undefined}
      ctaLabel={screen === 'landing' ? landingLabel : undefined}
    >
      <div style={styles.page}>
        {screen === 'loading' && (
          <div style={styles.center}>
            <p style={styles.mutedText}>Loading…</p>
          </div>
        )}

        {screen === 'not_found' && (
          <div style={styles.center}>
            <h1 style={styles.h1}>Page not found</h1>
            <p style={styles.mutedText}>This resource isn't available.</p>
          </div>
        )}

        {screen === 'coming_soon' && (
          <div style={styles.center}>
            <h1 style={styles.h1}>Coming soon</h1>
            <p style={styles.mutedText}>
              This summary is being prepared. Check back soon or{' '}
              <a href="/offers" style={styles.link}>see what's available now</a>.
            </p>
          </div>
        )}

        {screen === 'error' && (
          <div style={styles.center}>
            <h1 style={styles.h1}>Something went wrong</h1>
            <p style={styles.mutedText}>
              This page didn't load. Try again, or{' '}
              <a href="/offers" style={styles.link}>browse our resources</a>.
            </p>
            <button
              type="button"
              onClick={() => setRetryNonce((n) => n + 1)}
              style={{
                marginTop: 12,
                padding: '10px 24px',
                borderRadius: 8,
                border: 'none',
                background: '#F05A28',
                color: '#fff',
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
          </div>
        )}

        {screen === 'landing' && def && (() => {
          // CC authors the landing body with an {{OPT_IN_FORM}} marker; render
          // the live capture form exactly where the marker sits (or appended,
          // if absent). URL tokens were already resolved by the worker.
          const { before, after, label } = splitLanding(def.slug, def.landing_html ?? '');
          return (
            <div style={styles.content}>
              {/* CC authors the landing body — rendered as trusted HTML */}
              <div className="magnet-landing-body" dangerouslySetInnerHTML={{ __html: before }} />
              <div id="ee-optin" style={styles.captureBox}>
                <CaptureForm def={def} ctaLabel={label} onSuccess={handleCaptureSuccess} />
              </div>
              {after && <div className="magnet-landing-body" dangerouslySetInnerHTML={{ __html: after }} />}
            </div>
          );
        })()}

        {screen === 'thankyou' && def && (
          <div style={styles.content}>
            <FreeSeatThankyou freeSeat={freeSeat} />
            {thankyouHtml ? (
              <div
                className="magnet-thankyou-body"
                dangerouslySetInnerHTML={{ __html: thankyouHtml }}
              />
            ) : (
              <div style={styles.center}>
                <h1 style={styles.h1}>You're all set.</h1>
                <p style={styles.mutedText}>
                  Check your inbox — your summary is on its way.
                </p>
              </div>
            )}
            <CtaStack cta={cta} />
            <div style={{ maxWidth: 480, margin: '8px auto 0' }}>
              <SmsOptIn
                email={lead?.email ?? null}
                firstName={lead?.firstName ?? null}
                lastName={lead?.lastName ?? null}
                source="magnet"
                sourceDetail={def.slug}
                heading="Want a text when the next one drops?"
              />
            </div>
          </div>
        )}
      </div>
    </TakerShell>
  );
}

// ---------------------------------------------------------------------------
// Inline styles (light-only per EE convention)
// ---------------------------------------------------------------------------

const ACCENT = '#F05A28';
const BORDER = '#E5E7EB';

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: '#FAFAF7',
  },
  freeSeat: {
    maxWidth: 560,
    margin: '24px auto',
    padding: '16px 20px',
    border: `1px solid ${BORDER}`,
    borderRadius: 10,
    background: '#fff',
    textAlign: 'center',
  },
  optin: {
    display: 'flex',
    gap: 8,
    alignItems: 'flex-start',
    margin: '10px 0 4px',
    fontSize: 13,
    lineHeight: 1.45,
    textAlign: 'left',
  },
  content: {
    maxWidth: 680,
    margin: '0 auto',
    padding: '48px 24px 80px',
  },
  center: {
    maxWidth: 600,
    margin: '80px auto',
    textAlign: 'center',
    padding: '0 24px',
  },
  h1: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: 32,
    fontWeight: 700,
    color: '#111827',
    marginBottom: 12,
  },
  mutedText: {
    color: '#6B7280',
    fontSize: 16,
    lineHeight: 1.6,
  },
  link: {
    color: ACCENT,
    textDecoration: 'underline',
  },
  captureBox: {
    background: '#FFFFFF',
    border: `1px solid ${BORDER}`,
    borderRadius: 12,
    padding: '32px 28px',
    marginTop: 32,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  // Bare form: no card/field styling of our own — CC's form-card CSS paints the
  // inputs/button. Just stack with a small gap and let CC's margins do the rest.
  formBare: {
    display: 'block',
  },
  // Country <select> in bare mode: neutral so it sits in CC's card without
  // EE's orange-accent treatment.
  selectBare: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '13px 16px',
    fontSize: 15,
    fontFamily: 'inherit',
    border: '1px solid rgba(10,10,46,0.18)',
    borderRadius: 999,
    background: '#fff',
    marginBottom: 12,
    color: '#0A0A2E',
  },
  fieldRow: {
    display: 'flex',
    gap: 12,
  },
  input: {
    flex: 1,
    padding: '10px 14px',
    fontSize: 15,
    border: `1px solid ${BORDER}`,
    borderRadius: 8,
    outline: 'none',
    fontFamily: 'inherit',
    color: '#111827',
    background: '#FAFAF7',
    minWidth: 0,
  },
  submitBtn: {
    padding: '12px 20px',
    background: ACCENT,
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'opacity 0.15s',
  },
  errorText: {
    color: '#DC2626',
    fontSize: 14,
    margin: 0,
  },
  privacyNote: {
    color: '#9CA3AF',
    fontSize: 13,
    margin: 0,
    textAlign: 'center',
  },
  ctaStack: {
    marginTop: 48,
    display: 'flex',
    flexDirection: 'column',
    gap: 32,
  },
  ctaPrimary: {
    display: 'block',
    padding: '14px 24px',
    background: ACCENT,
    color: '#FFFFFF',
    textDecoration: 'none',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 16,
    textAlign: 'center',
  },
  ctaSecondary: {
    display: 'block',
    padding: '12px 20px',
    border: `1.5px solid ${ACCENT}`,
    color: ACCENT,
    textDecoration: 'none',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 15,
    textAlign: 'center',
  },
  ctaSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  ctaSectionLabel: {
    fontSize: 14,
    color: '#6B7280',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    margin: 0,
  },
  ctaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: 12,
  },
  ctaCard: {
    padding: '14px 16px',
    background: '#FFFFFF',
    border: `1px solid ${BORDER}`,
    borderRadius: 8,
    color: '#111827',
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.4,
    display: 'block',
  },
};
